import { useState, useEffect } from 'react';
import { useLocation, Link, useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet';

import type {
  Customer,
  Proposal,
  Installation,
  LetterOfAuthority,
  CustomerActivity,
} from '@/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { EmailTab } from '@/components/email/EmailTab';
import { CustomerDocuments } from '../components/CustomerDocuments';
import { LeadSourceCard } from '@/components/pipeline/LeadSourceCard';
import { CallButton } from '@/components/calling/CallButton';
import { formatAddress } from '@/utils/leadCard';
import {
  Users,
  FileText,
  PenTool,
  Send,
  Clipboard,
  Trash,
  Phone,
  Home,
  Calendar,
  Mail,
  MapPin,
  Printer,
  ArrowLeft,
  Play,
  ChevronDown,
  ChevronUp,
  Edit,
  Pencil,
  Save,
  X,
  Package,
  Settings,
  Activity,
  CheckCircle,
  Clock,
  HomeIcon,
  Bed,
  Store,
  CalendarRange,
  MessageSquare,
  FileIcon,
  Plus,
  Search,
  PhoneOff,
  Info,
} from 'lucide-react';

export default function CustomerMasterPage() {
  const { id } = useParams();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [notesValue, setNotesValue] = useState('');
  const [newActivityNote, setNewActivityNote] = useState('');

  // Fetch customer data
  const {
    data: customer,
    isLoading: customerLoading,
    error: customerError,
  } = useQuery({
    queryKey: [`/api/customers/${id}`],
    queryFn: () => apiRequest<Customer>('GET', `/api/customers/${id}`),
  });

  // Set notes value when customer data is loaded
  useEffect(() => {
    if (customer?.notes) {
      setNotesValue(customer.notes);
    }
  }, [customer]);

  // Fetch proposals for this customer
  const { data: proposals, isLoading: proposalsLoading } = useQuery({
    queryKey: ['/api/proposals', { customerId: id }],
    queryFn: () => apiRequest<Proposal[]>('GET', `/api/proposals?customerId=${id}`),
    enabled: !!id,
  });

  // Fetch installations for this customer
  const { data: installations, isLoading: installationsLoading } = useQuery({
    queryKey: ['/api/installations', { customerId: id }],
    queryFn: () => apiRequest<Installation[]>('GET', `/api/installations?customerId=${id}`),
    enabled: !!id,
  });

  // Fetch LOAs for this customer
  const { data: loas, isLoading: loasLoading } = useQuery({
    queryKey: ['/api/letters-of-authority', { customerId: id }],
    queryFn: () =>
      apiRequest<LetterOfAuthority[]>('GET', `/api/letters-of-authority?customerId=${id}`),
    enabled: !!id,
  });

  // Fetch pipeline stages for reference
  const { data: pipelineStages } = useQuery({
    queryKey: ['/api/pipeline-stages'],
    queryFn: () => apiRequest('GET', '/api/pipeline-stages'),
  });

  // Mutation for updating customer notes
  const updateNotesMutation = useMutation({
    mutationFn: (notes: string) => {
      return apiRequest('PATCH', `/api/customers/${id}`, {
        notes: notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/customers/${id}`] });
      toast({
        title: 'Notes updated',
        description: 'Customer notes have been updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update notes: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Mutation for updating customer details
  const updateCustomerMutation = useMutation({
    mutationFn: (data: Partial<Customer>) => {
      return apiRequest('PATCH', `/api/customers/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/customers/${id}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      setIsEditing(false);
      toast({
        title: 'Customer updated',
        description: 'Customer details have been updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to update customer: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Mutation for deleting customer
  const deleteCustomerMutation = useMutation({
    mutationFn: () => {
      return apiRequest('DELETE', `/api/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      navigate('/customers');
      toast({
        title: 'Customer deleted',
        description: 'Customer has been removed from the system',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to delete customer: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Handle form submission for editing customer details
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);

    // Process form data including nested additionalData fields
    const data: Record<string, any> = {};
    const additionalData: Record<string, any> = {};

    Array.from(formData.entries()).forEach(([key, value]) => {
      if (key.startsWith('additionalData.')) {
        const field = key.split('.')[1];
        additionalData[field] = value;
      } else {
        data[key] = value;
      }
    });

    // Only include additionalData if there are properties
    if (Object.keys(additionalData).length > 0) {
      data.additionalData = {
        ...customer?.additionalData,
        ...additionalData,
      };
    }

    updateCustomerMutation.mutate(data as Partial<Customer>);
  };

  // Handle deletion
  const handleDelete = () => {
    deleteCustomerMutation.mutate();
    setShowDeleteDialog(false);
  };

  // Handle saving notes
  const handleSaveNotes = () => {
    updateNotesMutation.mutate(notesValue);
  };

  // Handle saving an activity note
  const handleSaveActivity = () => {
    if (!newActivityNote.trim()) return;

    // For now, just append to notes - in a real implementation, this would create a proper activity record
    const updatedNotes = `${notesValue}\n\n[${new Date().toLocaleString()}] ${newActivityNote}`;
    updateNotesMutation.mutate(updatedNotes);
    setNewActivityNote('');
  };

  // Parse the additionalData object for display
  const additionalData = customer?.additionalData ? customer.additionalData : {};

  // Determine which icon to use for property type
  const getPropertyTypeIcon = (type: string | undefined) => {
    if (!type) return <HomeIcon />;

    type = type.toLowerCase();
    if (type.includes('flat') || type.includes('apartment')) return <Package />;
    if (type.includes('terraced')) return <Home />;
    if (type.includes('semi')) return <Home />;
    if (type.includes('detached')) return <Home />;
    if (type.includes('bungalow')) return <Home />;
    return <HomeIcon />;
  };

  // Get current pipeline stage name
  const getCurrentStageName = () => {
    if (!customer?.pipelineStageId || !pipelineStages) return 'Not in pipeline';
    const stage = pipelineStages.find((s: any) => s.id === customer.pipelineStageId);
    return stage ? stage.name : 'Unknown stage';
  };

  return (
    <div className="w-full">
      <Helmet>
        <title>{customer ? `${customer.name} | Solar CRM` : 'Customer Details | Solar CRM'}</title>
      </Helmet>

      {customerLoading ? (
        <div className="p-6 flex items-center justify-center">
          <div className="w-full max-w-4xl space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-64 w-full" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      ) : customerError ? (
        <div className="flex-1 p-6 flex items-center justify-center">
          <Card className="w-full max-w-4xl">
            <CardHeader>
              <CardTitle>Error Loading Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                There was an error loading the customer details. The customer may have been deleted
                or you may not have permission to view it.
              </p>
              <Button className="mt-4" onClick={() => navigate('/customers')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Customers
              </Button>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="w-full max-w-7xl mx-auto">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-4">
              <Link href="/customers" className="hover:text-slate-700 dark:hover:text-slate-300">
                Customers
              </Link>
              <span>&gt;</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">
                Master View: {customer.name}
              </span>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/customers')}
                  className="hidden md:flex"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center">
                  {customer.name}
                  <Badge className="ml-3 bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 px-2 py-0.5 text-xs font-medium">
                    Master View
                  </Badge>
                </h1>
                {customer.pipelineStageId && pipelineStages && (
                  <Badge
                    variant="outline"
                    className="ml-2 px-2.5 py-0.5"
                    style={{
                      backgroundColor:
                        pipelineStages.find((s: any) => s.id === customer.pipelineStageId)?.color +
                        '20',
                      color: pipelineStages.find((s: any) => s.id === customer.pipelineStageId)
                        ?.color,
                    }}
                  >
                    {getCurrentStageName()}
                  </Badge>
                )}
              </div>

              <div className="flex gap-2 self-end md:self-auto">
                {!isEditing ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                    >
                      <Trash className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Main Tabs Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="flex flex-wrap md:grid md:grid-cols-9 h-auto p-1 bg-slate-100 dark:bg-slate-800/50 rounded-lg mb-6 gap-1 overflow-x-auto">
                <TabsTrigger
                  value="details"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span className="text-xs">Details</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="activity"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Activity className="h-4 w-4" />
                    <span className="text-xs">Activity</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="projects"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <HomeIcon className="h-4 w-4" />
                    <span className="text-xs">Projects</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="proposals"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <FileText className="h-4 w-4" />
                    <span className="text-xs">Proposals</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="loas"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <PenTool className="h-4 w-4" />
                    <span className="text-xs">LOAs</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="installations"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Package className="h-4 w-4" />
                    <span className="text-xs">Installations</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <FileIcon className="h-4 w-4" />
                    <span className="text-xs">Documents</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="emails"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs">Emails</span>
                  </div>
                </TabsTrigger>
                <TabsTrigger
                  value="financials"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 px-2 py-2 rounded-md flex-shrink-0"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Clipboard className="h-4 w-4" />
                    <span className="text-xs">Financials</span>
                  </div>
                </TabsTrigger>
              </TabsList>

              {/* Tab Content */}
              <div className="grid grid-cols-1 gap-6 mb-6">
                {/* DETAILS TAB */}
                <TabsContent value="details" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Customer Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {isEditing ? (
                        <form onSubmit={handleSubmit}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-2">
                              <Label htmlFor="name">Full Name</Label>
                              <Input id="name" name="name" defaultValue={customer.name} required />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email">Email Address</Label>
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                defaultValue={customer.email || ''}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="phone">Phone Number</Label>
                              <Input id="phone" name="phone" defaultValue={customer.phone || ''} />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="address">Address</Label>
                              <Input
                                id="address"
                                name="address"
                                defaultValue={customer.address || ''}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="postcode">Postcode</Label>
                              <Input
                                id="postcode"
                                name="postcode"
                                defaultValue={customer.postcode || ''}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="source">Lead Source</Label>
                              <Input
                                id="source"
                                name="source"
                                defaultValue={customer.source || ''}
                              />
                            </div>
                          </div>

                          {/* Solar-specific Details Section */}
                          <div className="mt-6 mb-6">
                            <h3 className="text-md font-semibold mb-3">Solar Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="property_type">Property Type</Label>
                                <Input
                                  id="property_type"
                                  name="additionalData.property_type"
                                  defaultValue={customer?.additionalData?.property_type || ''}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="roof_type">Roof Type</Label>
                                <Input
                                  id="roof_type"
                                  name="additionalData.roof_type"
                                  defaultValue={customer?.additionalData?.roof_type || ''}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="roof_orientation">Roof Orientation</Label>
                                <Input
                                  id="roof_orientation"
                                  name="additionalData.roof_orientation"
                                  defaultValue={customer?.additionalData?.roof_orientation || ''}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="obstructions">Obstructions</Label>
                                <Input
                                  id="obstructions"
                                  name="additionalData.obstructions"
                                  defaultValue={customer?.additionalData?.obstructions || ''}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="energy_usage">Energy Usage (kWh)</Label>
                                <Input
                                  id="energy_usage"
                                  name="additionalData.energy_usage"
                                  defaultValue={customer?.additionalData?.energy_usage || ''}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="homeowner_status">Homeowner Status</Label>
                                <Input
                                  id="homeowner_status"
                                  name="additionalData.homeowner_status"
                                  defaultValue={customer?.additionalData?.homeowner_status || ''}
                                />
                              </div>
                            </div>
                          </div>

                          <Button type="submit" disabled={updateCustomerMutation.isPending}>
                            {updateCustomerMutation.isPending ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </form>
                      ) : (
                        <div className="space-y-6">
                          {/* Basic Info Section */}
                          <div>
                            <h3 className="text-md font-semibold mb-3">Contact Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-3">
                                {customer.email && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center shrink-0">
                                      <Mail className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Email
                                      </div>
                                      <div>{customer.email}</div>
                                    </div>
                                  </div>
                                )}

                                {customer.phone && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center shrink-0">
                                      <Phone className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Phone
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span>{customer.phone}</span>
                                        <CallButton
                                          customerId={customer.id}
                                          customerName={customer.name}
                                          customerPhone={customer.phone}
                                          size="sm"
                                          variant="outline"
                                          showLabel={true}
                                          className="h-7 px-2 text-xs gap-1 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:border-green-900 dark:hover:bg-green-900/50"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {(customer.address || customer.postcode) && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center shrink-0">
                                      <MapPin className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Address
                                      </div>
                                      <div>
                                        {formatAddress(customer.address, customer.postcode)}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {customer.source && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center shrink-0">
                                      <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Lead Source
                                      </div>
                                      <div>{customer.source}</div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Contact preferences section */}
                              {additionalData.contactPreference && (
                                <div>
                                  <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Contact Preferences
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {additionalData.contactPreference
                                      .toLowerCase()
                                      .includes('email') && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                                      >
                                        <Mail className="mr-1 h-3 w-3" /> Email
                                      </Badge>
                                    )}
                                    {additionalData.contactPreference
                                      .toLowerCase()
                                      .includes('phone') && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                                      >
                                        <Phone className="mr-1 h-3 w-3" /> Phone
                                      </Badge>
                                    )}
                                    {additionalData.contactPreference
                                      .toLowerCase()
                                      .includes('text') && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300"
                                      >
                                        <MessageSquare className="mr-1 h-3 w-3" /> Text
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Property information Section */}
                          <div>
                            <h3 className="text-md font-semibold mb-3">Property Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-3">
                                {additionalData.property_type && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      {getPropertyTypeIcon(additionalData.property_type)}
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Property Type
                                      </div>
                                      <div>{additionalData.property_type}</div>
                                    </div>
                                  </div>
                                )}

                                {additionalData.homeowner_status && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      <Home className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Homeowner Status
                                      </div>
                                      <div>{additionalData.homeowner_status}</div>
                                    </div>
                                  </div>
                                )}

                                {additionalData.bedrooms && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      <Bed className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Bedrooms
                                      </div>
                                      <div>{additionalData.bedrooms}</div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="space-y-3">
                                {additionalData.installation_timing && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      <CalendarRange className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Installation Timing
                                      </div>
                                      <div>{additionalData.installation_timing}</div>
                                    </div>
                                  </div>
                                )}

                                {additionalData.roof_type && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      <Home className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Roof Type
                                      </div>
                                      <div>{additionalData.roof_type}</div>
                                    </div>
                                  </div>
                                )}

                                {additionalData.roof_orientation && (
                                  <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <div className="w-8 h-8 rounded-full bg-green-50 dark:bg-green-950/50 flex items-center justify-center shrink-0">
                                      <Settings className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Roof Orientation
                                      </div>
                                      <div>{additionalData.roof_orientation}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ACTIVITY TAB */}
                <TabsContent value="activity" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Activity & Communication Log
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <Label htmlFor="new-activity-note">Add New Activity</Label>
                          <CallButton
                            customerId={customer?.id || 0}
                            customerName={customer?.name || ''}
                            customerPhone={customer?.phone}
                            size="sm"
                            variant="outline"
                            showLabel={true}
                            className="h-8 px-3 text-xs gap-1 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 dark:text-green-400 dark:border-green-900 dark:hover:bg-green-900/50"
                          />
                        </div>
                        <div className="flex gap-2 mt-1">
                          <Textarea
                            id="new-activity-note"
                            value={newActivityNote}
                            onChange={(e) => setNewActivityNote(e.target.value)}
                            placeholder="Enter details about a call, email, or other communication..."
                            className="min-h-[60px] resize-none flex-1"
                          />
                          <Button
                            className="self-end"
                            onClick={handleSaveActivity}
                            disabled={!newActivityNote.trim()}
                          >
                            Save Activity
                          </Button>
                        </div>
                      </div>

                      <Tabs defaultValue="notes" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="notes">Notes</TabsTrigger>
                          <TabsTrigger value="timeline">Timeline</TabsTrigger>
                        </TabsList>
                        <TabsContent value="notes" className="p-4">
                          <div className="space-y-4">
                            <Textarea
                              value={notesValue}
                              onChange={(e) => setNotesValue(e.target.value)}
                              placeholder="Enter notes about this customer"
                              className="min-h-[200px] resize-none"
                            />
                            <Button
                              onClick={handleSaveNotes}
                              disabled={updateNotesMutation.isPending}
                              size="sm"
                            >
                              {updateNotesMutation.isPending ? 'Saving...' : 'Save Notes'}
                            </Button>
                          </div>
                        </TabsContent>
                        <TabsContent value="timeline" className="p-4">
                          <ScrollArea className="h-[400px]">
                            <div className="space-y-4">
                              {/* Fetch and display customer activities */}
                              <CustomerActivities customerId={customer.id} />

                              {/* Display pipeline stage updates */}
                              {customer.pipelineUpdatedAt && (
                                <div className="flex gap-3">
                                  <div className="flex flex-col items-center">
                                    <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-900/50 flex items-center justify-center">
                                      <Activity className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                                    </div>
                                    <div className="w-0.5 h-full bg-slate-200 dark:bg-slate-700"></div>
                                  </div>
                                  <div className="space-y-1">
                                    <div className="text-sm font-medium">
                                      Pipeline Stage Updated
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                      {new Date(customer.pipelineUpdatedAt).toLocaleDateString()}
                                    </div>
                                    <div className="text-sm mt-1">
                                      Customer moved to {getCurrentStageName()} stage
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                  {customer?.id && (
                    <LeadSourceCard customerId={customer.id} customerSource={customer.source} />
                  )}
                </TabsContent>

                {/* PROJECTS TAB */}
                <TabsContent value="projects" className="mt-0">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <HomeIcon className="h-5 w-5" />
                        Projects
                      </CardTitle>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Project
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {/* No projects placeholder */}
                      <div className="flex flex-col items-center justify-center py-8">
                        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                          <HomeIcon className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                          No Projects Yet
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                          Projects represent solar installations or opportunities for this customer.
                          Create your first project to get started.
                        </p>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Create First Project
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* PROPOSALS TAB */}
                <TabsContent value="proposals" className="mt-0">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Proposals
                      </CardTitle>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Proposal
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {proposalsLoading ? (
                        <div className="space-y-4">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : proposals && proposals.length > 0 ? (
                        <div className="space-y-4">
                          {proposals.map((proposal) => (
                            <div
                              key={proposal.id}
                              className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                              onClick={() => navigate(`/proposals/${proposal.id}`)}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-medium">{proposal.title}</h3>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                      <Badge
                                        variant={
                                          proposal.status === 'accepted'
                                            ? 'success'
                                            : proposal.status === 'draft'
                                              ? 'outline'
                                              : 'secondary'
                                        }
                                      >
                                        {proposal.status.charAt(0).toUpperCase() +
                                          proposal.status.slice(1)}
                                      </Badge>
                                    </span>
                                    {proposal.totalPrice && (
                                      <span className="text-sm text-slate-500 dark:text-slate-400">
                                        £{(proposal.totalPrice / 100).toFixed(2)}
                                      </span>
                                    )}
                                    {proposal.createdAt && (
                                      <span className="text-sm text-slate-500 dark:text-slate-400">
                                        Created {new Date(proposal.createdAt).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Button variant="ghost" size="icon">
                                  <ArrowLeft className="h-4 w-4 rotate-180" />
                                  <span className="sr-only">View</span>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                            <FileText className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                          </div>
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                            No Proposals Yet
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                            Create your first proposal for this customer to get started with their
                            solar journey.
                          </p>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Create First Proposal
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* LOAs TAB */}
                <TabsContent value="loas" className="mt-0">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <PenTool className="h-5 w-5" />
                        Letters of Authority
                      </CardTitle>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New LOA
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {loasLoading ? (
                        <div className="space-y-4">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : loas && loas.length > 0 ? (
                        <div className="space-y-4">
                          {loas.map((loa) => (
                            <div
                              key={loa.id}
                              className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                              onClick={() => navigate(`/loa/${loa.id}`)}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-medium">
                                    Letter of Authority{' '}
                                    {loa.authorizedPersonName
                                      ? `- ${loa.authorizedPersonName}`
                                      : ''}
                                  </h3>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                      <Badge
                                        variant={
                                          loa.status === 'signed'
                                            ? 'success'
                                            : loa.status === 'draft'
                                              ? 'outline'
                                              : 'secondary'
                                        }
                                      >
                                        {loa.status.charAt(0).toUpperCase() + loa.status.slice(1)}
                                      </Badge>
                                    </span>
                                    {loa.dateOfSigning && (
                                      <span className="text-sm text-slate-500 dark:text-slate-400">
                                        Signed {new Date(loa.dateOfSigning).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Button variant="ghost" size="icon">
                                  <ArrowLeft className="h-4 w-4 rotate-180" />
                                  <span className="sr-only">View</span>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                            <PenTool className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                          </div>
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                            No Letters of Authority
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                            Create a letter of authority to proceed with the customer's project.
                          </p>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Letter of Authority
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* INSTALLATIONS TAB */}
                <TabsContent value="installations" className="mt-0">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Installations
                      </CardTitle>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Installation
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {installationsLoading ? (
                        <div className="space-y-4">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : installations && installations.length > 0 ? (
                        <div className="space-y-4">
                          {installations.map((installation) => (
                            <div
                              key={installation.id}
                              className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition cursor-pointer"
                              onClick={() => navigate(`/installations/${installation.id}`)}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-medium">
                                    {installation.title || `Installation #${installation.id}`}
                                  </h3>
                                  <div className="flex items-center gap-4 mt-1">
                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                      <Badge
                                        variant={
                                          installation.status === 'completed'
                                            ? 'success'
                                            : installation.status === 'scheduled'
                                              ? 'outline'
                                              : 'secondary'
                                        }
                                      >
                                        {installation.status.charAt(0).toUpperCase() +
                                          installation.status.slice(1)}
                                      </Badge>
                                    </span>
                                    {installation.installationDate && (
                                      <span className="text-sm text-slate-500 dark:text-slate-400">
                                        Scheduled for{' '}
                                        {new Date(
                                          installation.installationDate
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Button variant="ghost" size="icon">
                                  <ArrowLeft className="h-4 w-4 rotate-180" />
                                  <span className="sr-only">View</span>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8">
                          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                            <Package className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                          </div>
                          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                            No Installations Yet
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                            Once the customer has accepted a proposal, you can schedule their
                            installation here.
                          </p>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Installation
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* DOCUMENTS TAB */}
                <TabsContent value="documents" className="mt-0">
                  {customer && <CustomerDocuments customerId={customer.id} />}
                </TabsContent>

                {/* EMAILS TAB */}
                <TabsContent value="emails" className="mt-0">
                  {customer && <EmailTab customerId={customer.id} />}
                </TabsContent>

                {/* FINANCIALS TAB */}
                <TabsContent value="financials" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clipboard className="h-5 w-5" />
                        Financial Overview
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-md font-semibold mb-3">Contract Value</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card className="bg-slate-50 dark:bg-slate-800/50">
                              <CardContent className="pt-6">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  Total Contract Value
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                  {customer.dealValue
                                    ? `£${(customer.dealValue / 100).toFixed(2)}`
                                    : '£0.00'}
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="bg-slate-50 dark:bg-slate-800/50">
                              <CardContent className="pt-6">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  Invoiced Amount
                                </div>
                                <div className="text-2xl font-bold mt-1">£0.00</div>
                              </CardContent>
                            </Card>

                            <Card className="bg-slate-50 dark:bg-slate-800/50">
                              <CardContent className="pt-6">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                  Payment Status
                                </div>
                                <div className="mt-1">
                                  <Badge variant="outline">No payments</Badge>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-md font-semibold mb-3">Invoices</h3>
                          <div className="flex flex-col items-center justify-center py-8 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                              <Clipboard className="h-6 w-6 text-slate-500 dark:text-slate-400" />
                            </div>
                            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-2">
                              No Invoices Yet
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                              Once a contract is signed, you can create invoices for this customer.
                            </p>
                            <Button>
                              <Plus className="h-4 w-4 mr-2" />
                              Create Invoice
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </Tabs>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the customer record and all associated data. This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}
    </div>
  );
}

// CustomerActivities component to display activity timeline with Twilio calls and AI summaries
function CustomerActivities({ customerId }: { customerId: number }) {
  const [expandedActivity, setExpandedActivity] = useState<number | null>(null);
  const [editingActivityId, setEditingActivityId] = useState<number | null>(null);
  const [editedSummary, setEditedSummary] = useState('');
  const { toast } = useToast();

  // Fetch customer activities from the API
  const {
    data: activities,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/twilio/activities', customerId],
    queryFn: () => apiRequest('GET', `/api/twilio/activities/${customerId}`),
  });

  // Mutation for updating activity summary
  const updateActivityMutation = useMutation({
    mutationFn: async ({ activityId, summary }: { activityId: number; summary: string }) => {
      return apiRequest('PATCH', `/api/twilio/activities/${activityId}`, { summary });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/twilio/activities', customerId] });
      setEditingActivityId(null);
      setEditedSummary('');
      toast({
        title: 'Summary updated',
        description: 'The call summary has been updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update summary',
        variant: 'destructive',
      });
    },
  });

  // Start editing a summary
  const startEditing = (activity: CustomerActivity) => {
    setEditingActivityId(activity.id);
    setEditedSummary(activity.summary || '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingActivityId(null);
    setEditedSummary('');
  };

  // Save edited summary
  const saveEditedSummary = (activityId: number) => {
    if (!editedSummary.trim()) {
      toast({
        title: 'Error',
        description: 'Summary cannot be empty',
        variant: 'destructive',
      });
      return;
    }
    updateActivityMutation.mutate({ activityId, summary: editedSummary });
  };

  // Check if activity has been edited (has original AI summary in metadata)
  const isEdited = (activity: CustomerActivity) => {
    const metadata = activity.metadata as Record<string, unknown> | null;
    return metadata?.originalAiSummary !== undefined;
  };

  // Get original AI summary from metadata
  const getOriginalSummary = (activity: CustomerActivity) => {
    const metadata = activity.metadata as Record<string, unknown> | null;
    return metadata?.originalAiSummary as string | undefined;
  };

  // Function to toggle activity expansion (to show full content)
  const toggleExpand = (activityId: number) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };

  // Function to get the icon based on activity type
  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'call':
        return <Phone className="h-4 w-4 text-green-700 dark:text-green-300" />;
      case 'note':
        return <FileText className="h-4 w-4 text-amber-700 dark:text-amber-300" />;
      case 'email':
        return <Mail className="h-4 w-4 text-purple-700 dark:text-purple-300" />;
      default:
        return <Activity className="h-4 w-4 text-blue-700 dark:text-blue-300" />;
    }
  };

  // Function to get the background color based on activity type
  const getActivityBgColor = (activityType: string) => {
    switch (activityType) {
      case 'call':
        return 'bg-green-50 dark:bg-green-900/50';
      case 'note':
        return 'bg-amber-50 dark:bg-amber-900/50';
      case 'email':
        return 'bg-purple-50 dark:bg-purple-900/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/50';
    }
  };

  // Loading state
  if (isLoading) {
    return <div className="py-4 text-center text-slate-500">Loading activities...</div>;
  }

  // Error state
  if (error) {
    return <div className="py-4 text-center text-red-500">Error loading activities</div>;
  }

  // No activities state
  if (!activities || activities.length === 0) {
    return (
      <div className="flex justify-center items-center p-4 text-slate-500">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Open modal to add a note or initiate a call
            // This would be connected to the same functionality as the call/note buttons
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      </div>
    );
  }

  // Render activities timeline
  return (
    <>
      {activities.map((activity: CustomerActivity) => (
        <div key={activity.id} className="flex gap-3 mb-4">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full ${getActivityBgColor(activity.activityType)} flex items-center justify-center`}
            >
              {getActivityIcon(activity.activityType)}
            </div>
            <div className="w-0.5 h-full bg-slate-200 dark:bg-slate-700"></div>
          </div>

          <div className="space-y-1 flex-1">
            <div className="text-sm font-medium flex justify-between">
              <span>
                {activity.activityType === 'call'
                  ? 'Phone Call'
                  : activity.activityType === 'note'
                    ? 'Note Added'
                    : activity.activityType === 'email'
                      ? 'Email'
                      : 'Activity'}
              </span>

              {activity.recordingUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 p-0 px-2"
                  onClick={() => window.open(activity.recordingUrl, '_blank')}
                >
                  <Play className="h-3 w-3 mr-1" />
                  <span className="text-xs">Play Recording</span>
                </Button>
              )}
            </div>

            <div className="text-xs text-slate-500 dark:text-slate-400">
              {activity.createdAt && typeof activity.createdAt === 'string'
                ? new Date(activity.createdAt).toLocaleString()
                : activity.createdAt instanceof Date
                  ? activity.createdAt.toLocaleString()
                  : 'No timestamp'}
            </div>

            {/* Editable summary section for call activities */}
            {activity.activityType === 'call' && activity.summary && (
              <div className="mt-1">
                {editingActivityId === activity.id ? (
                  // Editing mode
                  <div className="space-y-2">
                    <Textarea
                      value={editedSummary}
                      onChange={(e) => setEditedSummary(e.target.value)}
                      className="min-h-[100px] text-sm"
                      placeholder="Enter call summary..."
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveEditedSummary(activity.id)}
                        disabled={updateActivityMutation.isPending}
                      >
                        {updateActivityMutation.isPending ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelEditing}
                        disabled={updateActivityMutation.isPending}
                      >
                        Cancel
                      </Button>
                      {isEdited(activity) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-2 gap-1 text-slate-500">
                                <Info className="h-3.5 w-3.5" />
                                <span className="text-xs">View original</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-sm">
                              <p className="text-xs font-medium mb-1">Original AI Summary:</p>
                              <p className="text-xs">{getOriginalSummary(activity)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                ) : (
                  // Display mode with edit button
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 text-sm">
                      <span className="font-medium">
                        AI Summary:
                        {isEdited(activity) && (
                          <Badge variant="outline" className="ml-2 text-xs px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                            Edited
                          </Badge>
                        )}
                      </span>{' '}
                      <span className="line-clamp-2">{activity.summary}</span>
                      {isEdited(activity) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-5 px-1 ml-1 text-slate-400 hover:text-slate-600">
                                <Info className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-sm">
                              <p className="text-xs font-medium mb-1">Original AI Summary:</p>
                              <p className="text-xs">{getOriginalSummary(activity)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      onClick={() => startEditing(activity)}
                      title="Edit summary"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            <Collapsible
              open={expandedActivity === activity.id}
              onOpenChange={() => toggleExpand(activity.id)}
              className="mt-1"
            >
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 p-0 hover:bg-transparent">
                  <div className="text-sm text-left line-clamp-2">
                    {activity.activityType === 'call' ? 'View transcript' : (activity.summary || activity.content || 'No content')}
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 ml-1 transition-transform ${
                      expandedActivity === activity.id ? 'rotate-180' : ''
                    }`}
                  />
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className="pt-2">
                <div className="text-sm bg-slate-50 dark:bg-slate-800/50 p-3 rounded">
                  {activity.aiSummary && (
                    <div className="mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
                      <div className="text-xs font-medium mb-1">AI Summary:</div>
                      <div>{activity.aiSummary}</div>
                    </div>
                  )}

                  <div>{activity.content || 'No detailed content available'}</div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      ))}
    </>
  );
}
